var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import styled from 'styled-components';
import { breakpoints, containerWidths, cssColorsFromBackgroundField, minWidth, radius as radii, scrollMargin, spacing } from '../../styles';
export const Container = styled.div `
  margin-left: auto;
  margin-right: auto;
  padding-left: 1rem;
  padding-right: 1rem;
  width: 100%;

  @media ${minWidth.sm} {
    max-width: ${containerWidths.sm}px;
  }

  @media ${minWidth.md} {
    max-width: ${containerWidths.md}px;
  }

  @media ${minWidth.lg} {
    max-width: ${containerWidths.lg}px;
  }

  @media ${minWidth.xl} {
    max-width: ${containerWidths.xl}px;
  }
`;
export const Row = styled.div `
  ${({ verticalAlign = 'stretch' }) => {
    switch (verticalAlign.toLowerCase()) {
        case 'top':
            return 'align-items: flex-start;';
        case 'bottom':
            return 'align-items: flex-end;';
        case 'center':
            return 'align-items: center;';
        default:
            return 'align-items: stretch;';
    }
}}
  ${({ align = 'left' }) => {
    switch (align.toLowerCase()) {
        case 'center':
            return 'justify-content: center;';
        case 'right':
            return 'justify-content: flex-end;';
        default:
            return 'justify-content: flex-start;';
    }
}}

  display: flex;
  flex-wrap: wrap;
  margin-left: -1rem;
  margin-right: -1rem;
`;
export function isColumnProperties(properties) {
    return !!properties; // TODO: check properties is actually UiColumnProperties;
}
//up to 5 decimals in percentage widths for webkit (eg 7 => 58.333333%)
function spanWidth(span) {
    const regex = RegExp(/^-?\d+(?:\.\d{0,5})?/);
    const percentage = regex.exec(((parseInt(span) * 100) / 12).toString());
    return percentage ? `${percentage[0]}%` : 'auto';
}
function breakpointToCSS({ align, offset, push, order, span, top, bottom, left, right, radius }) {
    const css = [];
    if (align) {
        css.push(`text-align: ${align};`);
    }
    if (offset) {
        css.push(`margin-left: ${spanWidth(offset)};`);
    }
    if (push) {
        css.push(`left: ${spanWidth(push)};`);
    }
    if (order) {
        css.push(`order: ${order};`);
    }
    if (span) {
        css.push(span === '0'
            ? `
      display: none;
      `
            : `
      display: block;
      flex: 0 0 auto;
      width: ${spanWidth(span)};
    `);
    }
    if (top) {
        if (top.substr(0, 1) === '-') {
            //z-index added to keep negative-margin div on top
            css.push(`
        margin-top: -${spacing[top.substr(1)]};
        z-index: 1;
      `);
        }
        else {
            css.push(`padding-top: ${spacing[top]};`);
        }
    }
    if (bottom) {
        //z-index added to keep negative-margin div on top
        if (bottom.substr(0, 1) === '-') {
            css.push(`
        margin-bottom: -${spacing[bottom.substr(1)]};
        z-index: 1;
      `);
        }
        else {
            css.push(`padding-bottom: ${spacing[bottom]};`);
        }
    }
    if (left) {
        //z-index added to keep negative-margin div on top
        if (left.substr(0, 1) === '-') {
            css.push(`
        margin-left: -${spacing[left.substr(1)]};
        z-index: 1;
      `);
        }
        else {
            css.push(`padding-left: ${spacing[left]};`);
        }
    }
    if (right) {
        //z-index added to keep negative-margin div on top
        if (right.substr(0, 1) === '-') {
            css.push(`
        margin-right: -${spacing[right.substr(1)]};
        z-index: 1;
      `);
        }
        else {
            css.push(`padding-right: ${spacing[right]};`);
        }
    }
    if (radius) {
        css.push(`border-radius: ${radii[radius]};`);
    }
    return css.join('');
}
export const Column = (_a) => {
    var { children, className: classesFromProps = '' } = _a, props = __rest(_a, ["children", "className"]);
    function addClassNames() {
        var _a;
        let classNames = classesFromProps;
        const { xs, sm, md, lg, xl } = props;
        for (const [key, value] of Object.entries({ xs, sm, md, lg, xl })) {
            if (!!value && ((_a = value === null || value === void 0 ? void 0 : value.align) === null || _a === void 0 ? void 0 : _a.length)) {
                classNames += ` ${key}-text-${value.align}`;
            }
        }
        return classNames.toLowerCase();
    }
    return (_jsx(StyledColumn, Object.assign({ className: addClassNames() }, props, { children: children }), void 0));
};
const StyledColumn = styled.div `
  padding: 1rem;
  position: relative;
  scroll-margin-top: ${scrollMargin.top}px;
  text-align: center;
  width: 100%;

  /* prevent last internal block from adding bottom margin (eg webinars page) */
  > *:last-child {
    margin-bottom: 0;
    /* prevent last element inside that from adding bottom margin (p tag inside a markdown for example) */
    > *:last-child {
      margin-bottom: 0;
      /* prevent last element inside that from adding bottom margin (p tag inside a markdown for example) */
      > *:last-child {
        margin-bottom: 0;
      }
    }
  }

  ${({ backgroundField }) => cssColorsFromBackgroundField(backgroundField)}

  ${({ xs }) => xs && breakpointToCSS(xs)}

  @media (min-width: ${breakpoints.sm}px) {
    ${({ sm }) => sm && breakpointToCSS(sm)}
  }

  @media (min-width: ${breakpoints.md}px) {
    ${({ md }) => md && breakpointToCSS(md)}
  }

  @media (min-width: ${breakpoints.lg}px) {
    ${({ lg }) => lg && breakpointToCSS(lg)}
  }

  @media (min-width: ${breakpoints.xl}px) {
    ${({ xl }) => xl && breakpointToCSS(xl)}
  }
`;
//# sourceMappingURL=grid.js.map